import React, { useState, useMemo } from 'react';
import { formatRate, formatSize } from 'utils/formatUtils';
import {
  getRate,
  getTotalRate,
  getLicenseRecommendation
} from 'utils/licenseUtils';

import LicenseCard from '../LicenseCards';
import {
  DataGridBody,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridHeaderRow,
  DataGridRow
} from 'components/DataGrid';

import {
  StyledMetricsTable,
  DisplayCell,
  NumberOfFeaturesCell,
  StyledTextField,
  TimeUnitCell,
  StyledTotalRow,
  TotalCell,
  TotalRateCell,
  StyledHeading,
  StyledParagraph,
  StyledNotice,
  StyledSelect
} from './LicenseCalculator-styled';

import { CalciteModal, CalciteOption } from 'components/Calcite';

interface LicenseCalculatorProps {
  metricsCollection: Metrics[];
  onRequestClose: () => void;
}

type NumberOfFeaturesUpdate = {
  numberOfFeatures: number;
};

type TimeUnitUpdate = {
  timeUnit: TimeUnit;
};

const DEFAULTS: CalculationConfig = {
  averageSizeOfFeaturesBytes: 0,
  numberOfFeatures: null,
  timeUnit: null,
  rate: null
};

const LicenseCalculator: React.FC<LicenseCalculatorProps> = ({
  metricsCollection,
  onRequestClose
}) => {
  const [calculations, setCalculations] = useState<CalculationConfig[]>(
    metricsCollection.map(({ averageSizeOfFeaturesBytes }) => {
      const { numberOfFeatures, timeUnit } = DEFAULTS;
      return {
        averageSizeOfFeaturesBytes,
        numberOfFeatures,
        timeUnit: timeUnit,
        rate: getRate(numberOfFeatures, timeUnit, averageSizeOfFeaturesBytes)
      };
    })
  );

  const totalRate: number | null = useMemo(
    () => getTotalRate(calculations),
    [calculations]
  );

  const licenseRecommendation: LicenseRecommendation | null = useMemo(
    () => getLicenseRecommendation(totalRate, calculations.length),
    [totalRate, calculations]
  );

  const updateConfig = (
    update: NumberOfFeaturesUpdate | TimeUnitUpdate,
    idx: number
  ) => {
    const config = calculations[idx];
    const { numberOfFeatures, timeUnit } = { ...config, ...update };
    const newConfig: CalculationConfig = {
      ...config,
      numberOfFeatures,
      timeUnit,
      rate: getRate(
        numberOfFeatures,
        timeUnit,
        metricsCollection[idx].averageSizeOfFeaturesBytes
      )
    };
    const configsCopy = [...calculations];
    configsCopy.splice(idx, 1, newConfig);
    setCalculations(configsCopy);
  };

  return (
    <CalciteModal
      open
      onClose={onRequestClose}
      outsideCloseDisabled
      title="License Calculator"
      widthScale="l"
    >
      <div slot="content">
        <StyledMetricsTable>
          <DataGridHeader>
            <DataGridHeaderRow>
              <DataGridHeaderCell>Average Size</DataGridHeaderCell>
              <DataGridHeaderCell>Number of Features</DataGridHeaderCell>
              <DataGridHeaderCell>Time Unit</DataGridHeaderCell>
              <DataGridHeaderCell>Rate</DataGridHeaderCell>
            </DataGridHeaderRow>
          </DataGridHeader>
          <DataGridBody>
            {calculations.map(
              (
                {
                  averageSizeOfFeaturesBytes,
                  numberOfFeatures,
                  timeUnit,
                  rate
                },
                idx
              ) => (
                <DataGridRow key={idx}>
                  <>
                    <DisplayCell>
                      {formatSize(averageSizeOfFeaturesBytes)}
                    </DisplayCell>
                    <NumberOfFeaturesCell>
                      <StyledTextField
                        status={numberOfFeatures ? 'idle' : 'invalid'}
                        value={
                          numberOfFeatures === null
                            ? ''
                            : numberOfFeatures.toString()
                        }
                        onChange={value =>
                          updateConfig(
                            {
                              numberOfFeatures: Number(value) || 0
                            },
                            idx
                          )
                        }
                      />
                    </NumberOfFeaturesCell>
                    <TimeUnitCell>
                      <StyledSelect
                        label="timeunit"
                        value={timeUnit as string}
                        onChange={value =>
                          updateConfig({ timeUnit: value as TimeUnit }, idx)
                        }
                        options={[
                          <CalciteOption
                            key="second"
                            selected={timeUnit === 'second'}
                            value="second"
                          >
                            Second
                          </CalciteOption>,
                          <CalciteOption
                            key="minute"
                            selected={timeUnit === 'minute'}
                            value="minute"
                          >
                            Minute
                          </CalciteOption>,
                          <CalciteOption
                            key="hour"
                            selected={timeUnit === 'hour'}
                            value="hour"
                          >
                            Hour
                          </CalciteOption>
                        ]}
                      />
                    </TimeUnitCell>
                    <DisplayCell>
                      {rate === null ? '---' : formatRate(rate)}
                    </DisplayCell>
                  </>
                </DataGridRow>
              )
            )}
            <StyledTotalRow key="total">
              <TotalCell colSpan={3}>
                TOTAL ({calculations.length} Items)
              </TotalCell>
              <TotalRateCell>
                {totalRate === null ? '---' : formatRate(totalRate, true)}
              </TotalRateCell>
            </StyledTotalRow>
          </DataGridBody>
        </StyledMetricsTable>
        <StyledHeading>Recommended License Type</StyledHeading>
        {licenseRecommendation?.hint ? (
          <StyledParagraph
            dangerouslySetInnerHTML={{ __html: licenseRecommendation.hint }}
          />
        ) : (
          <StyledNotice kind="danger" open width="half">
            <div slot="message">
              Please provide the number of features and the time unit for all
              your calculations
            </div>
          </StyledNotice>
        )}
        <LicenseCard licenseType={licenseRecommendation?.type || null} />
      </div>
    </CalciteModal>
  );
};

export default LicenseCalculator;
